import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AudioTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/audio-tracking/audio-tracking.component';
import { CaptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/caption/caption.component';
import { ChatbotMultiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.component';
import { ChatbotComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.component';
import { ContentComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/content/content.component';
import { CustomComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/custom/custom.component';
import {
  TDeliverable,
  TDeliverableForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { DeliverableListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.service';
import { DescriptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/description/description.component';
import { GamePlayComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.component';
import { HashtagComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/hashtag/hashtag.component';
import { MentionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/mention/mention.component';
import { MessageComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/message/message.component';
import { ProductPlacementComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.component';
import { ShowcaseComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/showcase/showcase.component';
import { TalkingPointAiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point-ai/talking-point-ai.component';
import { TalkingPointComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/talking-point/talking-point.component';
import { TextTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/text-tracking/text-tracking.component';
import { TitleComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/title/title.component';
import { VisualComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.component';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { IBriefStepState } from '../brief-step.interface';

@Component({
  imports: [
    ReactiveFormsModule,
    AudioTrackingComponent,
    CaptionComponent,
    ChatbotComponent,
    ChatbotMultiComponent,
    ContentComponent,
    CustomComponent,
    DescriptionComponent,
    GamePlayComponent,
    HashtagComponent,
    MentionComponent,
    MessageComponent,
    ProductPlacementComponent,
    ShowcaseComponent,
    TalkingPointAiComponent,
    TalkingPointComponent,
    TextTrackingComponent,
    TitleComponent,
    VisualComponent,
  ],
  selector: 'app-brief-deliverable-item-step',
  templateUrl: './deliverable-item-step.component.html',
})
export class BriefDeliverableItemStepComponent {
  public readonly type = input.required<EDeliverableType>();
  // Typed loosely rather than as TDeliverable: this comes straight off the brief's denormalized
  // draft, which the deliverable-array flattening only tags with `type` - it doesn't (and can't)
  // reconstruct the full discriminated-union shape TDeliverable expects for every field.
  // DeliverableListService.createDeliverable is tolerant of a partial item (see its `item?`
  // param), so this is safe to pass through as-is.
  public readonly initialValue = input.required<Record<string, unknown>>();
  public readonly platformFeature = input<EPlatformFeature | null>(null);
  public readonly stateChange = output<IBriefStepState<Record<string, unknown>>>();

  public readonly EDeliverableType = EDeliverableType;

  // A computed (not a plain field reassigned inside an effect) so every consumer - this
  // template's [form] binding, and nested leaf components' own [control]="form().get(...)"
  // bindings on mat-error - is guaranteed to read the SAME, current FormGroup instance. A plain
  // mutable field can leave a child's regular @Input() setter (e.g. mat-error's `control`)
  // pointing at an abandoned FormGroup from an earlier recompute while the rest of the form
  // has already moved on to a newer one, silently hiding validation errors.
  public readonly form = computed<FormGroup<TDeliverableForm> | null>(() => {
    const type = this.type();
    const initialValue = this.initialValue();

    return this.deliverableListService.createDeliverable(type, initialValue as unknown as TDeliverable);
  });

  private readonly deliverableListService = inject(DeliverableListService);

  constructor() {
    effect((onCleanup) => {
      const form = this.form();

      if (!form) {
        return;
      }

      form.markAllAsTouched();
      this.emitState(form);

      // The real leaf component (e.g. GamePlayComponent) that mounts right after this - and
      // reads `form` via [form]="deliverableForm" - extends CustomCtaBaseDirective, whose own
      // ngOnInit() unconditionally calls form.markAsUntouched()/markAsPristine() (correct for its
      // normal campaign-form usage: a freshly-added deliverable shouldn't show errors before the
      // user touches it). That ngOnInit runs synchronously right after this effect, during the
      // same render, so it silently undoes the markAllAsTouched() above - Material's own mat-error
      // then stays hidden even though the control genuinely has errors. Re-touching one macrotask
      // later, after that ngOnInit has already run, is what actually makes the error visible.
      const timeoutId = setTimeout(() => form.markAllAsTouched());

      const subscription = form.valueChanges.subscribe(() => this.emitState(form));

      onCleanup(() => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
      });
    });
  }

  private emitState(form: FormGroup<TDeliverableForm>): void {
    this.stateChange.emit({ valid: form.valid, value: form.getRawValue() as unknown as Record<string, unknown> });
  }
}
